import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getOrganizerStats, getOrganizerEvents, getOrganizerMessages, publishEvent, submitEvent, markMessageRead } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus,
  Calendar,
  DollarSign,
  Ticket,
  TrendingUp,
  Eye,
  Edit,
  Send,
  Globe,
  BarChart3,
  ArrowRight,
  MessageCircle,
  CheckCircle,
  Clock,
  Users,
  QrCode,
  Mail,
  Phone,
  ChevronRight
} from 'lucide-react';

const OrganizerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, messagesRes] = await Promise.all([
        getOrganizerStats(),
        getOrganizerMessages()
      ]);
      setStats(statsRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForReview = async (eventId) => {
    try {
      await submitEvent(eventId);
      toast.success('Evento enviado para revisión');
      loadData();
    } catch (error) {
      toast.error('Error al enviar evento');
    }
  };

  const handlePublish = async (eventId) => {
    try {
      await publishEvent(eventId);
      toast.success('¡Evento publicado exitosamente!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al publicar evento');
    }
  };

  const handleViewMessage = async (message) => {
    setSelectedMessage(message);
    if (message.status === 'unread') {
      try {
        await markMessageRead(message.message_id);
        setMessages(prev => prev.map(m => 
          m.message_id === message.message_id ? { ...m, status: 'read' } : m
        ));
      } catch (error) {
        console.error('Error marking message as read:', error);
      }
    }
  };

  const formatPrice = (price) => {
    return `L. ${(price || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusBadge = (event) => {
    if (event.is_finished) {
      return <Badge className="bg-gray-500/10 text-gray-500 border-0">Finalizado</Badge>;
    }
    if (event.is_active) {
      return <Badge className="bg-green-500/10 text-green-500 border-0">En Venta</Badge>;
    }
    switch (event.status) {
      case 'draft':
        return <Badge className="bg-gray-500/10 text-gray-500 border-0">Borrador</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/10 text-yellow-500 border-0">En Revisión</Badge>;
      case 'approved':
        return <Badge className="bg-blue-500/10 text-blue-500 border-0">Aprobado</Badge>;
      default:
        return <Badge className="bg-primary/10 text-primary border-0">{event.status}</Badge>;
    }
  };

  const getMessageTypeBadge = (type) => {
    switch (type) {
      case 'info':
        return <Badge className="bg-blue-500/10 text-blue-500 border-0">Información</Badge>;
      case 'tickets':
        return <Badge className="bg-orange-500/10 text-orange-500 border-0">Boletos</Badge>;
      default:
        return <Badge className="bg-purple-500/10 text-purple-500 border-0">Otra</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-12 px-4 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const summary = stats?.summary || {};
  const events = stats?.events || [];
  const activeEvents = events.filter(e => e.is_active);
  const finishedEvents = events.filter(e => e.is_finished);
  const pendingEvents = events.filter(e => !e.is_active && !e.is_finished);
  const unreadMessages = messages.filter(m => m.status === 'unread');

  return (
    <div className="min-h-screen pt-20 pb-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">Dashboard del Organizador</h1>
            <p className="text-muted-foreground mt-1">Bienvenido, {user?.name}</p>
          </div>
          <Link to="/organizer/events/new">
            <Button className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Crear Evento
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Eventos Activos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-500">{summary.active_events || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">En venta ahora</p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Ticket className="w-4 h-4" />
                Tickets Vendidos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-primary">{summary.total_tickets_sold || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">
                de {summary.total_tickets_available || 0} disponibles
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Ingresos Totales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-orange-500">{formatPrice(summary.total_revenue)}</p>
              <p className="text-xs text-muted-foreground mt-1">{summary.total_orders || 0} órdenes</p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Visitas Totales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{summary.total_views || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">en todos los eventos</p>
            </CardContent>
          </Card>
        </div>

        {/* Messages Alert */}
        {unreadMessages.length > 0 && (
          <div 
            className="mb-6 p-4 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-between cursor-pointer hover:bg-primary/15 transition-colors"
            onClick={() => setActiveTab('messages')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold">Tienes {unreadMessages.length} mensaje{unreadMessages.length > 1 ? 's' : ''} nuevo{unreadMessages.length > 1 ? 's' : ''}</p>
                <p className="text-sm text-muted-foreground">Haz clic para ver los mensajes de tus compradores</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Resumen</span>
            </TabsTrigger>
            <TabsTrigger value="active" className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              <span className="hidden sm:inline">Activos</span>
              {activeEvents.length > 0 && (
                <Badge className="ml-1 bg-green-500 text-white text-xs px-1.5">{activeEvents.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="finished" className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Finalizados</span>
            </TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Mensajes</span>
              {unreadMessages.length > 0 && (
                <Badge className="ml-1 bg-primary text-white text-xs px-1.5">{unreadMessages.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <QrCode className="w-5 h-5 text-green-500" />
                    Tickets Escaneados
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-2">
                    <p className="text-4xl font-bold text-green-500">{summary.scanned_tickets || 0}</p>
                    <p className="text-muted-foreground mb-1">/ {summary.total_finished_tickets || 0}</p>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">en eventos finalizados</p>
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-yellow-500" />
                    Eventos Pendientes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-yellow-500">{summary.pending_events || 0}</p>
                  <p className="text-sm text-muted-foreground mt-2">esperando aprobación</p>
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    Eventos Finalizados
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-blue-500">{summary.finished_events || 0}</p>
                  <p className="text-sm text-muted-foreground mt-2">completados exitosamente</p>
                </CardContent>
              </Card>
            </div>

            {/* All Events List */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Todos los Eventos</span>
                  <Badge variant="outline">{events.length} total</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {events.slice(0, 5).map((event) => (
                    <div 
                      key={event.event_id}
                      className="flex items-center justify-between p-4 rounded-xl border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{event.title}</h3>
                          {getStatusBadge(event)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(event.date), "d MMM yyyy", { locale: es })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Ticket className="w-3 h-3" />
                            {event.sold_tickets}/{event.total_tickets}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {event.views}
                          </span>
                          <span className="flex items-center gap-1 text-green-500">
                            <DollarSign className="w-3 h-3" />
                            {formatPrice(event.revenue)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {event.status === 'draft' && (
                          <Button size="sm" variant="outline" onClick={() => handleSubmitForReview(event.event_id)}>
                            <Send className="w-3 h-3 mr-1" />
                            Enviar
                          </Button>
                        )}
                        {event.status === 'approved' && (
                          <Button size="sm" className="btn-primary" onClick={() => handlePublish(event.event_id)}>
                            <Globe className="w-3 h-3 mr-1" />
                            Publicar
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/organizer/events/${event.event_id}/edit`)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Active Events Tab */}
          <TabsContent value="active" className="space-y-4">
            {activeEvents.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="p-12 text-center">
                  <Globe className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No hay eventos activos</h3>
                  <p className="text-muted-foreground mb-4">Crea y publica un evento para comenzar a vender tickets</p>
                  <Link to="/organizer/events/new">
                    <Button className="btn-primary">
                      <Plus className="w-4 h-4 mr-2" />
                      Crear Evento
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              activeEvents.map((event) => (
                <Card key={event.event_id} className="glass-card">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-xl font-bold">{event.title}</h3>
                          <Badge className="bg-green-500/10 text-green-500 border-0">En Venta</Badge>
                        </div>
                        <p className="text-muted-foreground">
                          {format(new Date(event.date), "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })}
                        </p>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-primary">{event.sold_tickets}</p>
                          <p className="text-xs text-muted-foreground">de {event.total_tickets} vendidos</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-orange-500">{formatPrice(event.revenue)}</p>
                          <p className="text-xs text-muted-foreground">ingresos</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold">{event.views}</p>
                          <p className="text-xs text-muted-foreground">visitas</p>
                        </div>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Progreso de ventas</span>
                        <span className="font-medium">{Math.round((event.sold_tickets / event.total_tickets) * 100)}%</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-primary to-orange-500 rounded-full transition-all"
                          style={{ width: `${(event.sold_tickets / event.total_tickets) * 100}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Finished Events Tab */}
          <TabsContent value="finished" className="space-y-4">
            {finishedEvents.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="p-12 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No hay eventos finalizados</h3>
                  <p className="text-muted-foreground">Los eventos completados aparecerán aquí</p>
                </CardContent>
              </Card>
            ) : (
              finishedEvents.map((event) => (
                <Card key={event.event_id} className="glass-card">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-xl font-bold">{event.title}</h3>
                          <Badge className="bg-gray-500/10 text-gray-500 border-0">Finalizado</Badge>
                        </div>
                        <p className="text-muted-foreground">
                          {format(new Date(event.date), "d 'de' MMMM 'de' yyyy", { locale: es })}
                        </p>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-primary">{event.sold_tickets}</p>
                          <p className="text-xs text-muted-foreground">tickets vendidos</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-orange-500">{formatPrice(event.revenue)}</p>
                          <p className="text-xs text-muted-foreground">ingresos totales</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-500">{event.orders_count}</p>
                          <p className="text-xs text-muted-foreground">órdenes</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-4">
            {messages.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="p-12 text-center">
                  <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No hay mensajes</h3>
                  <p className="text-muted-foreground">Los mensajes de tus compradores aparecerán aquí</p>
                </CardContent>
              </Card>
            ) : (
              messages.map((msg) => (
                <Card 
                  key={msg.message_id} 
                  className={`glass-card cursor-pointer hover:border-primary/50 transition-colors ${msg.status === 'unread' ? 'border-primary/30 bg-primary/5' : ''}`}
                  onClick={() => handleViewMessage(msg)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{msg.sender_name}</h3>
                          {getMessageTypeBadge(msg.message_type)}
                          {msg.status === 'unread' && (
                            <span className="w-2 h-2 bg-primary rounded-full" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{msg.event_title}</p>
                        <p className="text-sm line-clamp-2">{msg.message}</p>
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        {format(new Date(msg.created_at), "d MMM", { locale: es })}
                        <br />
                        {format(new Date(msg.created_at), "HH:mm")}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Message Detail Dialog */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-primary" />
              Mensaje de {selectedMessage?.sender_name}
            </DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-muted/50">
                <div className="flex items-center gap-2 mb-2">
                  {getMessageTypeBadge(selectedMessage.message_type)}
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(selectedMessage.created_at), "d 'de' MMMM 'de' yyyy 'a las' HH:mm", { locale: es })}
                  </span>
                </div>
                <p className="font-medium mb-1">{selectedMessage.event_title}</p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Mensaje:</h4>
                <p className="text-muted-foreground whitespace-pre-wrap">{selectedMessage.message}</p>
              </div>

              <div className="p-4 rounded-xl border border-border space-y-2">
                <h4 className="font-semibold">Datos de contacto:</h4>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <a href={`mailto:${selectedMessage.sender_email}`} className="text-primary hover:underline">
                    {selectedMessage.sender_email}
                  </a>
                </div>
                {selectedMessage.sender_phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a href={`tel:${selectedMessage.sender_phone}`} className="text-primary hover:underline">
                      {selectedMessage.sender_phone}
                    </a>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button 
                  className="flex-1"
                  onClick={() => window.location.href = `mailto:${selectedMessage.sender_email}?subject=Re: ${selectedMessage.event_title}`}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Responder por Email
                </Button>
                {selectedMessage.sender_phone && (
                  <Button 
                    variant="outline"
                    onClick={() => window.location.href = `https://wa.me/${selectedMessage.sender_phone.replace(/\D/g, '')}`}
                  >
                    <MessageCircle className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizerDashboard;
